import { useEffect, useState, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { useLocation } from "react-router-dom";
import Distinctions from "./Distinctions";
import AppTour from "../../../../components/AppTour/AppTour";
import { contentViewerTourSteps } from "../../../../components/AppTour/ModuleTourSteps";
import { useContents } from "../../../../queries/professoru/contents.api";
import MusicLoader from "../../DataAnalyst/sections/components/MusicLoader";
import { useProfessorUActivityTracker } from "../../../../hooks/useProfessorUActivityTracker";
import useVideoProgress from "../../../../hooks/useVideoProgress";
import useYouTubePlayer from "../../../../hooks/useYouTubePlayer";
import useScrollProgress from "../../../../hooks/useScrollProgress";

const getYouTubeVideoId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
};

export default function InterviewAndGuideViewer({ type }) {
    const [runTour, setRunTour] = useState(false);
    const location = useLocation();
    const [htmlContent, setHtmlContent] = useState('');
    const params = new URLSearchParams(location.search);
    const pageContent = params.get("content");
    const moduleIdParam = params.get("moduleId");
    const moduleId = [null, "", "null", "undefined"].includes(moduleIdParam) ? null : moduleIdParam;
    const contentId = params.get("contentId")
    const [activeModuleId, setActiveModuleId] = useState(moduleId);
    const [activeContentId, setActiveContentId] = useState(contentId);
    const contentType = type || params.get("type") || "interview";
    const { data: contentsResponse, isPending, isFetching } = useContents(
        { type: contentType, limit: 100 },
        { enabled: !!contentType }
    );

    const videoRef = useRef(null);
    const htmlContainerRef = useRef(null);
    const pdfTimerRef = useRef(null);
    const pdfCompletedRef = useRef(false);

    const { trackGuideRead, trackTalkWatched } = useProfessorUActivityTracker();
    
    useEffect(() => {
        setActiveModuleId(moduleId);
        setActiveContentId(contentId);
    }, [moduleId, contentId]);

    // Find current index based on contentId
    const contents = (contentsResponse?.data || []).filter(item => item.type === contentType);
    const currentIndex = contents.findIndex(
        item => item._id === activeContentId && (!activeModuleId || item.moduleId === activeModuleId)
    );
    const currentContent = currentIndex !== -1 ? contents[currentIndex] : null;
    const contentUrl = currentContent?.content?.mediaUrl || '';

    const handleContentCompleted = useCallback(() => {
        if (!currentContent) return;
        const contentId = currentContent._id;
        const moduleId = currentContent.moduleId;
        const type = currentContent.type;

        if (contentType === "interview") {
            trackTalkWatched(contentId, moduleId, type);
        } else {
            trackGuideRead(contentId, moduleId, type);
        }
    }, [currentContent, contentType, trackTalkWatched, trackGuideRead]);

    const youtubeVideoId = contentUrl ? getYouTubeVideoId(contentUrl) : null;
    const { containerRef: youtubeContainerRef, playerRef } = useYouTubePlayer(youtubeVideoId);

    const getVideoElement = useCallback(() => videoRef.current, []);
    const getPlayer = useCallback(() => playerRef.current, [playerRef]);

    useVideoProgress({
        getVideoElement,
        getPlayer,
        onComplete: handleContentCompleted,
        threshold: 0.9,
        contentId: activeContentId,
    });

    useScrollProgress({
        ref: htmlContainerRef,
        onComplete: handleContentCompleted,
        threshold: 0.8,
        minTimeMs: 15000,
    });

    useEffect(() => {
        pdfCompletedRef.current = false;
        if (pdfTimerRef.current) {
            clearTimeout(pdfTimerRef.current);
            pdfTimerRef.current = null;
        }
    }, [activeContentId, contentUrl]);

    const next = () => {
        if (currentIndex < contents.length - 1) {
            const nextContent = contents[currentIndex + 1];
            setActiveModuleId(nextContent.moduleId);
            setActiveContentId(nextContent._id);
        }
    };

    const prev = () => {
        if (currentIndex > 0) {
            const prevContent = contents[currentIndex - 1];
            setActiveModuleId(prevContent.moduleId);
            setActiveContentId(prevContent._id);
        }
    };

    const shouldFetchAsHtml = (url) => {
        if (!url) return false;
        const lower = url.toLowerCase();
        if (/youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed/.test(lower)) return false;
        if (/\.(mp4|webm|avi|mov|mkv)(\?|$)/.test(lower)) return false;
        if (/\.(pdf|doc|docx)(\?|$)/.test(lower)) return false;
        return true;
    };

    useEffect(() => {
        if (!contentUrl || !shouldFetchAsHtml(contentUrl)) {
            setHtmlContent('');
            return;
        }
        fetch(contentUrl)
            .then(res => res.text())
            .then(data => setHtmlContent(data))
            .catch(err => console.error('Failed to load HTML:', err));
    }, [contentUrl]);

    // Detect content type and return appropriate component
    const getYouTubeEmbedUrl = (url) => {
        const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
        const match = url.match(youtubeRegex);
        return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    };

    const detectContentType = (url) => {
        if (!url) return null;

        const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();

        if (getYouTubeEmbedUrl(url)) return 'youtube';

        if (cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.avi')) return 'video';
        if (cleanUrl.endsWith('.pdf')) return 'pdf';
        if (cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx')) return 'document';
        if (cleanUrl.endsWith('.html') || cleanUrl.endsWith('.htm')) return 'html';

        // If URL starts with http and has no file extension at the end, assume HTML
        if (cleanUrl.startsWith('http') && !/\.[a-zA-Z0-9]{2,6}$/.test(cleanUrl)) return 'html';

        return 'text';
    };

    const renderContent = () => {
        if (isPending || isFetching) {
            return (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    <MusicLoader />
                </div>
            )
        }
        if (!contentUrl) {
            return (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    No content available
                </div>
            );
        }

        const contentType = detectContentType(contentUrl);
        const sanitizedHtml = htmlContent
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/onclick="[^"]*"/gi, '');

        switch (contentType) {
            case 'youtube':
                return (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <div
                            ref={youtubeContainerRef}
                            className="w-full h-full"
                            style={{ minHeight: '400px' }}
                        />
                    </div>
                );

            case 'video':
                return (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <video
                            ref={videoRef}
                            width="100%"
                            height="100%"
                            controls
                            style={{ maxHeight: '400px', objectFit: 'contain' }}
                        >
                            <source src={contentUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                );

            case 'pdf':
                return (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <iframe
                            title={currentContent?.title || "PDF content"}
                            src={contentUrl}
                            width="100%"
                            height="100%"
                            style={{ minHeight: '400px' }}
                            onLoad={() => {
                                if (pdfCompletedRef.current) return;
                                pdfTimerRef.current = setTimeout(() => {
                                    pdfCompletedRef.current = true;
                                    handleContentCompleted();
                                }, 15000);
                            }}
                        />
                    </div>
                );

            case 'document':
                return (
                    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                        <FileText className="text-gray-400 w-16 h-16 mb-4" />
                        <p className="text-gray-600 mb-4">Document Preview</p>
                        <a
                            href={contentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-theme_clr1 text-white px-6 py-2 rounded-lg hover:opacity-80 transition"
                            onClick={handleContentCompleted}
                        >
                            Download Document
                        </a>
                    </div>
                );

            case 'html':
                return (
                    <div ref={htmlContainerRef} className="flex-1 overflow-auto p-6 bg-white">
                        <div className="max-w-4xl mx-auto">
                            <div
                                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                                className="guide-content"
                            />
                        </div>
                    </div>
                );

            default:
                // Text content
                return (
                    <div className="flex-1 overflow-auto p-6">
                        <div className="text-gray-800 whitespace-pre-wrap break-words">
                            {contentUrl}
                        </div>
                    </div>
                );
        }
    };

    useEffect(() => {
        if (location.hash) {
            setRunTour(true)
        }
    }, [location.hash])

    return (
        <div className="DataAnalyst-page py-5">
            <AppTour
                tourSteps={contentViewerTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
                tourName="professoru-interview-guide-viewer"
                moduleId={activeModuleId || "professoru-interview-guide-viewer"}
            />
            <div className="container max-w-full">
                <Distinctions
                    title={pageContent}
                    inputPlaceholder={"What do you want to achieve right now?"}
                />
                <div className="w-full bg-theme_clr1 rounded-2xl p-6 flex flex-col items-center mt-4" data-tour="content-viewer">

                    {/* Title Pill */}
                    <div className="bg-theme_clr2 text-white px-8 py-2 rounded-lg font-semibold mb-6">
                        {currentContent?.title || pageContent || "Loading..."}
                    </div>

                    {/* Content Area */}
                    <div className="w-full bg-gray-200 rounded-xl h-[500px] relative overflow-hidden flex flex-col">
                        {/* Content Renderer */}
                        {renderContent()}
                    </div>

                    {/* Bottom Center Arrows */}
                    <div className="relative z-10 flex w-full min-h-[54px] flex-shrink-0 justify-center items-center gap-16 mt-4" data-tour="content-navigation">

                        {/* LEFT */}
                        <button
                            onClick={prev}
                            disabled={currentIndex <= 0}
                            className={`p-3 rounded-full transition
        ${currentIndex <= 0
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-white hover:bg-gray-300"
                                }`}
                        >
                            <ChevronLeft size={30} />
                        </button>

                        {/* RIGHT */}
                        <button
                            onClick={next}
                            disabled={currentIndex === -1 || currentIndex >= contents.length - 1}
                            className={`p-3 rounded-full transition
        ${currentIndex === -1 || currentIndex >= contents.length - 1
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-white hover:bg-gray-300"
                                }`}
                        >
                            <ChevronRight size={30} />
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
}